import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DataContext } from '../contexts/DataProvider';
import { ChatContext } from '../types/chat';
import { AGENT_SESSION_STORAGE_KEY } from '../services/AgentChatClient';

const getInitialSessionId = (): string => {
  try {
    const stored = localStorage.getItem(AGENT_SESSION_STORAGE_KEY);
    if (stored) return stored;
  } catch { /* private browsing — ignore */ }
  return generateSessionId();
};

export const generateSessionId = (): string =>
  `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;


export const useChatContext = (): ChatContext => {
  const location = useLocation();
  const { login } = useContext(DataContext);
  const [chatContext, setChatContext] = useState<Omit<ChatContext, 'resetSessionId' | 'loadSessionId'>>({
    sessionId: getInitialSessionId(),
    currentRoute: location.pathname,
    routeParams: {},
    currentUser: login,
    timestamp: new Date()
  });

  const resetSessionId = () => {
    setChatContext(prev => ({ ...prev, sessionId: generateSessionId() }));
  };

  const loadSessionId = (id: string) => {
    setChatContext(prev => ({ ...prev, sessionId: id }));
  };

  useEffect(() => {
    const routeParams = extractRouteParams(location.pathname, location.search);
    setChatContext(prev => ({
      ...prev,
      currentRoute: location.pathname,
      routeParams,
      currentUser: login,
      timestamp: new Date()
    }));
  }, [location, login]);

  useEffect(() => {
    const handlePrivateFileSelection = (event: Event) => {
      const detail = (event as CustomEvent<ChatContext['selectedPrivateFile']>).detail;
      setChatContext((prev) => ({
        ...prev,
        selectedPrivateFile: detail || null,
        timestamp: new Date(),
      }));
    };
    window.addEventListener('prospo-private-file-selection', handlePrivateFileSelection);
    return () => window.removeEventListener('prospo-private-file-selection', handlePrivateFileSelection);
  }, []);

  return { ...chatContext, resetSessionId, loadSessionId };
};

const extractRouteParams = (pathname: string, search: string) => {
  const params: Record<string, string> = {};
  const urlParams = new URLSearchParams(search);
  urlParams.forEach((value, key) => { params[key] = value; });
  const pathSegments = pathname.split('/').filter(Boolean);
  if (pathSegments.length >= 2) {
    const [resource, id] = pathSegments;
    if (id && id !== 'new') params[`${resource}Id`] = id;
  }
  return params;
};
